// ─────────────────────────────────────────────────────────────────────────
//  Apocalypse — native iOS / Safari FX abuses.
//
//  Four orthogonal effects the magician can layer onto the cinematic
//  glitch. Each is enabled independently from Settings. Each function
//  is defensively wrapped so a permission-denied or unsupported-API
//  branch silently no-ops without ever breaking the rest of the trick.
//
//  Effects:
//    • permissionPromptFx — calls a sensitive Web API (camera / mic /
//      notifications / geolocation) which makes iOS pop the REAL system
//      permission sheet over the page. Looks like malware.
//    • speakRevealFx       — speechSynthesis "Decryption complete…"
//    • writeClipboardFx    — silently writes the card name to the
//      clipboard so the spectator can paste it anywhere afterwards.
//      Requires a recent user gesture; must be called from the unlock
//      handler (LockScreen.jsx), NOT from inside a setTimeout chain.
//    • themeColorFlickerFx — animates <meta name="theme-color"> which
//      in PWA / home-screen mode bends the *real* iOS status bar
//      background to whatever color we want.
// ─────────────────────────────────────────────────────────────────────────

// ---- 1) Permission prompt theatre --------------------------------------
//
// Trigger the system permission sheet for the chosen API. The sheet is
// real iOS UI floating above the PWA. We don't actually use the
// permission — we just need the dialog to appear. Returns void; the
// dialog itself blocks the spectator until they tap Allow / Don't Allow.
//
// Caveats (worth surfacing in Settings):
//  - If the user has previously granted or denied the permission on
//    THIS origin, iOS won't show the dialog again. To re-arm: long-
//    press the home-screen icon → Site Settings → Reset.
//  - getUserMedia + geolocation require a "transient user activation"
//    — meaning we must be inside a synchronous descendant of a user
//    gesture. We call this from the start of the wipe phase, which
//    happens ~50ms after the unlock tap → still inside the activation.
export function triggerPermissionPromptFx(type) {
  if (typeof window === "undefined") return;
  try {
    if (type === "camera" || type === "microphone") {
      // We do NOT keep the stream. As soon as the user resolves the
      // sheet (allow OR deny), we stop every track and walk away.
      if (!navigator.mediaDevices?.getUserMedia) return;
      const constraints =
        type === "camera" ? { video: true } : { audio: true };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          stream.getTracks().forEach((t) => {
            try { t.stop(); } catch { /* ignore */ }
          });
        })
        .catch(() => { /* denied or unsupported — fine */ });
    } else if (type === "notifications") {
      if (typeof Notification === "undefined") return;
      // requestPermission can be promise or callback-style depending
      // on Safari version; handle both shapes.
      const p = Notification.requestPermission();
      if (p && typeof p.then === "function") p.catch(() => {});
    } else if (type === "geolocation") {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        () => {},
        () => {},
        { timeout: 1, maximumAge: 60000 }
      );
    }
  } catch {
    // Anything throws → silent. Trick continues without the FX.
  }
}

// ---- 2) Speech-synthesis reveal ----------------------------------------
//
// Speak the configured template into the phone's speaker. Works in iOS
// Safari and installed PWAs. Voice is selected by URI; if the URI isn't
// available we fall back to the system default.
export function speakRevealFx(text, { voiceUri, rate, pitch, lang } = {}) {
  if (typeof window === "undefined") return;
  if (!("speechSynthesis" in window)) return;
  if (!text) return;
  try {
    const synth = window.speechSynthesis;
    // Drop anything queued so the magician can't accidentally hear the
    // previous run's reveal stacked on top of this one.
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    if (typeof rate === "number") utter.rate = rate;
    if (typeof pitch === "number") utter.pitch = pitch;
    if (lang) utter.lang = lang;
    if (voiceUri) {
      const voices = synth.getVoices();
      const match = voices.find((v) => v.voiceURI === voiceUri);
      if (match) utter.voice = match;
    }
    synth.speak(utter);
  } catch {
    // ignore
  }
}

export function cancelSpeechFx() {
  if (typeof window === "undefined") return;
  if (!("speechSynthesis" in window)) return;
  try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
}

// ---- 3) Clipboard hijack ----------------------------------------------
//
// MUST be called from inside a user-gesture handler (the unlock button
// tap on LockScreen). Outside that window iOS Safari rejects writeText
// with NotAllowedError.
export function writeClipboardFx(text) {
  if (typeof navigator === "undefined") return Promise.resolve(false);
  if (!navigator.clipboard?.writeText) return Promise.resolve(false);
  return navigator.clipboard
    .writeText(text)
    .then(() => true)
    .catch(() => false);
}

// ---- 4) Theme-color status bar flicker --------------------------------
//
// On a PWA installed to the iOS home screen, <meta name="theme-color">
// controls the actual native status bar background. We swap it through
// a cycle of colors at a fixed interval; the returned function stops
// the cycle and restores the original color.
//
// Falls back to a no-op cleanly when the meta tag is missing or we're
// in plain Safari (still useful — many in-app browsers honor it).
export function startThemeColorFlickerFx(colors, intervalMs) {
  if (typeof document === "undefined") return () => {};
  if (!Array.isArray(colors) || colors.length === 0) return () => {};
  let meta = document.querySelector('meta[name="theme-color"]');
  let created = false;
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
    created = true;
  }
  const original = meta.getAttribute("content") || "";
  let i = 0;
  const tick = () => {
    meta.setAttribute("content", colors[i % colors.length]);
    i += 1;
  };
  tick();
  const id = setInterval(tick, Math.max(50, intervalMs | 0));
  return () => {
    clearInterval(id);
    if (created) {
      meta.parentNode?.removeChild(meta);
    } else {
      meta.setAttribute("content", original);
    }
  };
}

